import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { LuLoaderCircle, LuScanFace, LuShieldCheck, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import { useTranslation } from '../../i18n/LanguageContext';

/** After the first password login: register the face (passkey) on this device, or skip. */
const EnrollPanel = ({ busy, error, onEnroll, onSkip }) => {
  const { t } = useTranslation();
  return (
    <div className="tw-space-y-5 tw-text-center">
      <div className="tw-relative tw-mx-auto tw-grid tw-h-20 tw-w-20 tw-place-items-center">
        <motion.span
          aria-hidden="true"
          className="tw-absolute tw-inset-0 tw-rounded-full tw-border-2 tw-border-teal-400/50"
          animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0, 0.7] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="tw-grid tw-h-20 tw-w-20 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
          <LuScanFace aria-hidden="true" className="tw-h-9 tw-w-9" />
        </span>
      </div>

      <div className="tw-space-y-2">
        <h2 className="tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{t('login.enroll.title')}</h2>
        <p className="tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{t('login.enroll.body')}</p>
      </div>

      <p className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-2xl tw-bg-slate-900/[0.03] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-leading-relaxed tw-text-slate-600 dark:tw-bg-white/[0.04] dark:tw-text-slate-300">
        <LuShieldCheck aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-teal-600 dark:tw-text-teal-300" />
        {t('login.enroll.privacy')}
      </p>

      {error && (
        <p role="alert" className="tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-bg-amber-500/10 tw-px-3.5 tw-py-2.5 tw-text-left tw-text-sm tw-font-medium tw-text-amber-800 dark:tw-text-amber-200">
          <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
          {error}
        </p>
      )}

      <div className="tw-space-y-2">
        <Button variant="primary" size="lg" onClick={onEnroll} disabled={busy} className="tw-w-full tw-text-[15px]">
          {busy
            ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
            : <LuScanFace aria-hidden="true" className="tw-h-4 tw-w-4" />}
          {busy ? t('login.enroll.busy') : t('login.enroll.start')}
        </Button>
        <Button variant="ghost" size="lg" onClick={onSkip} disabled={busy} className="tw-w-full">
          {t('login.enroll.skip')}
        </Button>
      </div>
    </div>
  );
};

EnrollPanel.propTypes = {
  busy: PropTypes.bool,
  error: PropTypes.string,
  onEnroll: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default EnrollPanel;
